/**
 * ReconciliationBanner.tsx — what the totals check concluded, on the page where
 * the total is read.
 *
 * WHY THIS EXISTS
 * ---------------
 * The check itself shipped on 2026-08-12 and reproduced TORINO Phase 2's
 * € 2.941.125,57 over-extraction from the real PDF. Its verdict then reached
 * exactly one surface: the colour of a toast, shown once, only to whoever
 * happened to be watching when the job finished. Anyone who reloaded, or opened
 * the project the next morning, saw a wrong number with nothing beside it. A
 * check whose output nobody reads protects nobody — which is the same lesson,
 * one level up, as the rule that was written in three documents and no code.
 *
 * THE DESIGN RULE HERE: ABSENCE MUST NEVER READ AS A PASS.
 * That is why `ok` renders something rather than nothing. If a verified total
 * were silent, silence would mean both "checked, matches" and "never checked",
 * and the user could not tell which they were looking at. So every state is
 * stated, including the two that mean "the check could not run" — those are the
 * ones a reader would otherwise mistake for a clean bill of health.
 */
import { type AnalysisJobStatus, type ReconciliationVerdict } from "../../lib/tenderApi";
import { fmtEurCents } from "../../lib/format";

type Tone = "ok" | "warn" | "err" | "muted";

const TONE_STYLE: Record<Tone, { fg: string; bg: string; border: string }> = {
  ok: { fg: "var(--c-ok)", bg: "var(--c-ok-soft)", border: "rgba(47,125,78,0.30)" },
  warn: { fg: "var(--c-warn)", bg: "var(--c-warn-soft)", border: "rgba(184,115,12,0.32)" },
  err: { fg: "var(--c-err)", bg: "var(--c-err-soft)", border: "rgba(181,61,42,0.34)" },
  muted: { fg: "var(--c-ink-3)", bg: "var(--c-bg-elev)", border: "var(--c-line)" },
};

interface Copy {
  tone: Tone;
  title: string;
  detail: string;
}

/** The verdict in Italian, as a product statement rather than a status code.
 *
 * `verdict === null` is its own case and deliberately NOT silent: it means the
 * analysis predates the check (or failed before it ran), which is a different
 * thing from "the totals agree" and must not look like it.
 */
export function reconciliationCopy(
  verdict: ReconciliationVerdict | null,
  jobStatus?: AnalysisJobStatus | null,
): Copy | null {
  if (verdict === null) {
    // WHY THIS BRANCH SPLITS THREE WAYS. The banner reads the LATEST job, and
    // `/jobs/latest` has no status filter — but the figures below it come from
    // the persisted rows, which the next run only replaces at the very end.
    //
    // So the moment a user does what a red banner told them to do and starts a
    // re-run, the alarm turned grey and said "questa analisi è precedente al
    // controllo automatico" — about rows that had been checked and had failed.
    // Two false claims at once, for the 1–3 hours of the new run, and permanently
    // if that run dies. Saying which job we are talking about is the whole fix.
    if (jobStatus === "queued" || jobStatus === "running") {
      return {
        tone: "warn",
        title: "Analisi in corso — totali non riverificati",
        detail:
          "I valori mostrati provengono dall'analisi precedente e non sono stati " +
          "riverificati: l'analisi in corso non ha ancora prodotto un esito.",
      };
    }
    if (jobStatus === "failed") {
      return {
        tone: "warn",
        title: "Ultima analisi non riuscita — totali non riverificati",
        detail:
          "I valori mostrati provengono da un'analisi precedente. L'ultima analisi " +
          "non è arrivata a produrre un esito sui totali.",
      };
    }
    return {
      tone: "muted",
      title: "Totali non verificati",
      detail:
        "Questa analisi è precedente al controllo automatico dei totali. " +
        "Per verificarla, esegui di nuovo l'analisi.",
    };
  }

  const stated = verdict.stated;
  const page = verdict.stated_page;
  const onPage = page ? ` (pag. ${page})` : "";
  // Goal 55A, last round (Omar, 4 Oct): with amounts not read, every sum below is of the items
  // READ, and says so — never «the items' sum» while some are unknown.
  const unread = verdict.unread_amounts ?? 0;
  const unreadVoci = unread === 1 ? "1 voce" : `${unread} voci`;
  const readSum = unread > 0 ? "Le voci lette sommano" : "Le voci estratte sommano";

  switch (verdict.status) {
    case "ok":
      return {
        tone: "ok",
        title: "Totali verificati",
        detail:
          `La somma delle voci estratte corrisponde al totale dichiarato dal computo` +
          `${onPage}: ${fmtEurCents(verdict.extracted)}.`,
      };

    case "over_extracted":
      return {
        tone: "err",
        title: "Totale superiore a quello dichiarato dal computo",
        detail:
          `${readSum} ${fmtEurCents(verdict.extracted)}, ` +
          `ma il computo dichiara ${stated === null ? "—" : fmtEurCents(stated)}${onPage}. ` +
          `Differenza: ${fmtEurCents(Math.abs(verdict.residual ?? 0))}. ` +
          (unread > 0
            ? `${unreadVoci} con importo non letto non ${unread === 1 ? "è compresa" : "sono comprese"} nella somma. `
            : "") +
          (verdict.explanation ??
            "Alcune righe potrebbero essere state conteggiate due volte. " +
              "Non utilizzare questi importi prima della verifica."),
      };

    case "under_extracted": {
      // A shortfall can be legitimate — AOSTA's € 470.501,17 is a lump sum the
      // computo never breaks down. When the check can EXPLAIN the residual, say
      // so plainly; when it cannot, this is a possible extraction gap.
      //
      // Severity keys on `residual_explained`, NOT on `explanation !== null`.
      // The backend returns prose either way, including "this matches nothing,
      // rows were probably lost" — so keying on presence made the escalation
      // branch unreachable and headlined € 2.000.000 of dropped line items as
      // "differenza spiegata", directly contradicting its own body text.
      // `undefined`/`null` means a verdict predating the field: keep the old
      // behaviour rather than retroactively alarming every archived AOSTA run.
      const explained = verdict.residual_explained ?? verdict.explanation !== null;
      return {
        tone: explained ? "warn" : "err",
        title: explained
          ? "Totale inferiore al dichiarato — differenza spiegata"
          : "Totale inferiore a quello dichiarato dal computo",
        detail:
          `Le voci estratte sommano ${fmtEurCents(verdict.extracted)}, ` +
          `ma il computo dichiara ${stated === null ? "—" : fmtEurCents(stated)}${onPage}. ` +
          `Differenza: ${fmtEurCents(Math.abs(verdict.residual ?? 0))}. ` +
          (verdict.explanation ?? "Alcune voci potrebbero non essere state estratte."),
      };
    }

    case "unread_amounts": {
      // Goal 55A, last round (Omar, 4 Oct): some amounts were not read, so the sum of the
      // items is not known. Never «verificati», never «manca del denaro»: the items are there,
      // their amounts are unknown — and the explanation must not call one «non estratta».
      const n = verdict.unread_amounts ?? 0;
      return {
        tone: "warn",
        title: "Totali non verificabili",
        detail:
          `${n === 1 ? "1 voce" : `${n} voci`} con importo non letto: la somma delle voci ` +
          `non è determinabile. Le voci lette sommano ${fmtEurCents(verdict.extracted)}, ` +
          `il computo dichiara ${stated === null ? "—" : fmtEurCents(stated)}${onPage}: ` +
          `la quadratura non è verificabile.`,
      };
    }

    case "no_stated_total":
      return {
        tone: "warn",
        title: "Totali non verificabili",
        detail:
          "Non è stato trovato un totale dichiarato nel computo, quindi la somma delle voci " +
          (unread > 0
            ? `lette (${fmtEurCents(verdict.extracted)}; ${unreadVoci} con importo non letto) `
            : `(${fmtEurCents(verdict.extracted)}) `) +
          "non ha potuto essere confrontata con nulla.",
      };

    case "unreadable":
      return {
        tone: "warn",
        title: "Totali non verificabili",
        detail:
          "Non è stato possibile rileggere il computo per confrontare i totali. " +
          (unread > 0
            ? `La somma delle voci lette è ${fmtEurCents(verdict.extracted)} ` +
              `(${unreadVoci} con importo non letto).`
            : `La somma delle voci estratte è ${fmtEurCents(verdict.extracted)}.`),
      };

    default:
      // An unknown status from a newer backend. Never silently treated as a
      // pass — say what is known and name the state verbatim.
      return {
        tone: "warn",
        title: "Esito del controllo dei totali sconosciuto",
        detail:
          `Il controllo ha restituito lo stato "${verdict.status}", non riconosciuto da questa ` +
          `versione dell'applicazione. Somma delle voci: ${fmtEurCents(verdict.extracted)}.`,
      };
  }
}

interface Props {
  verdict: ReconciliationVerdict | null;
  /** False while the analysis has not produced any line items yet — a project
   * that was never analysed should show nothing at all, rather than
   * "not verified", which would read as a fault. */
  hasAnalysis: boolean;
  /** Status of the job the verdict was read from. Needed because a verdict can
   * be absent for three different reasons, and only one of them means "this
   * analysis predates the check". */
  jobStatus?: AnalysisJobStatus | null;
}

export function ReconciliationBanner({
  verdict,
  hasAnalysis,
  jobStatus,
}: Props): JSX.Element | null {
  if (!hasAnalysis) return null;
  const copy = reconciliationCopy(verdict, jobStatus);
  if (!copy) return null;
  const tone = TONE_STYLE[copy.tone];

  return (
    <div
      role={copy.tone === "err" ? "alert" : "status"}
      data-testid="reconciliation-banner"
      data-tone={copy.tone}
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        padding: "11px 14px",
        marginBottom: 16,
        borderRadius: 8,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: tone.fg }}>{copy.title}</div>
        <div
          style={{
            fontSize: 12.5,
            marginTop: 3,
            color: "var(--c-ink-2)",
            lineHeight: 1.5,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {copy.detail}
        </div>
      </div>
    </div>
  );
}
